/**
 * Proxy Client Service
 * Centralized HTTP client for OptLab Proxy
 */

import { getValidAccessToken } from "../shared/auth/authStore";
import { logger } from "../shared/logger";
import { logDebug } from "../shared/logging";
import { getPublicConfig } from "../excel/config/getPublicConfig";
import {
  diagnosticsStore,
  categorizeError,
  safeErrorMessage,
} from "../shared/diagnostics";
import type {
  PingResponse,
  AuthTestResponse,
  MassiveSearchResponse,
  MassiveItemResponse,
  MassiveOptionsResponse,
  MassiveMarketHolidaysResponse,
  MassiveMarketStatusNowResponse,
  ErrorResponse,
  ProxyResponse,
  EntitlementsMeResponse,
  CheckoutSessionResponse,
  BillingPortalResponse,
} from "../shared/types";

// Initialize diagnostics store on module load (idempotent, safe to call multiple times)
// This runs in both taskpane and functions contexts
void diagnosticsStore.init().catch(() => {
  // Swallow errors - init never throws, but Promise rejection could happen
});

// ============================================================================
// OpenClose Cache + In-Flight De-dupe + Concurrency Limiter
// ============================================================================

// Cache configuration
const CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes

// In-memory cache for OpenClose responses
const openCloseCache = new Map<string, { value: any; expiresAt: number }>();

// In-flight request de-duplication (prevents duplicate concurrent requests)
const openCloseInflight = new Map<string, Promise<any>>();

// Concurrency limiter configuration
const MAX_CONCURRENT = 5;
let active = 0;
const queue: Array<() => void> = [];

/**
 * Enqueue a function to run with concurrency limiting.
 * Ensures only MAX_CONCURRENT requests run at once.
 */
function enqueue<T>(fn: () => Promise<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    const run = () => {
      active++;
      fn()
        .then(resolve)
        .catch(reject)
        .finally(() => {
          active--;
          const next = queue.shift();
          if (next) next();
        });
    };
    if (active < MAX_CONCURRENT) {
      run();
    } else {
      queue.push(run);
    }
  });
}

// Resolve proxy base URL (async)
async function getProxyBaseUrl(): Promise<string> {
  const config = await getPublicConfig();
  return config.public.apiBaseUrl;
}

// Generate request ID
function generateRequestId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID
  return (
    Date.now().toString(36) +
    Math.random().toString(36).substring(2, 15) +
    Math.random().toString(36).substring(2, 15)
  );
}


// Typed error class for proxy client errors
export class ProxyClientError extends Error {
  code: string;
  status: number;
  requestId: string;
  retryable: boolean;
  details?: unknown; // Optional details for diagnostics (not shown in Excel cells)
  constructor(
    code: string,
    message: string,
    status: number,
    requestId: string,
    retryable: boolean,
    details?: unknown
  ) {
    super(message);
    this.name = "ProxyClientError";
    this.code = code;
    this.status = status;
    this.requestId = requestId;
    this.retryable = retryable;
    this.details = details;
  }
}

/**
 * Derive operation key from endpoint and method
 */
function deriveOpKey(endpointNormalized: string, method?: string): string {
  return (method || "GET") + " " + endpointNormalized;
}

/**
 * True when this is a GET to the Massive snapshot options endpoint.
 * Such requests use only simple headers to avoid CORS preflight (no Authorization, x-request-id, etc.).
 */
function isSimpleGetSnapshot(endpoint: string, method?: string): boolean {
  return isPublicNoAuthEndpoint(endpoint, method);
}

/** Exported for tests: true when endpoint is public and must not send auth (Massive snapshot GET). */
export function isPublicNoAuthEndpoint(endpoint: string, method?: string): boolean {
  const m = (method || "GET").toUpperCase();
  return m === "GET" && endpoint.startsWith("/v1/massive/v3/snapshot/options/");
}

/** Extended request options: RequestInit + optional diagnostics fields (not sent to fetch) */
export type ProxyRequestOptions = RequestInit & {
  sessionCacheOutcome?: "hit" | "miss" | "join" | "bypass";
  sessionJoinCountGetter?: () => number;
};

function normalizeCacheResult(raw: string | null): "hit" | "miss" | "bypass" | "stale" | "none" {
  const s = (raw || "").trim().toLowerCase();
  if (s === "hit" || s === "miss" || s === "bypass" || s === "stale") return s;
  return "none";
}

// Make a request to the proxy
async function request<T extends ProxyResponse>(
  endpoint: string,
  options: RequestInit | ProxyRequestOptions = {},
  opKey?: string
): Promise<T> {
  const opts = options as Record<string, unknown>;
  const sessionCacheOutcome = opts.sessionCacheOutcome as "hit" | "miss" | "join" | "bypass" | undefined;
  const sessionJoinCountGetter = opts.sessionJoinCountGetter as (() => number) | undefined;
  const { sessionCacheOutcome: _d1, sessionJoinCountGetter: _d2, ...fetchOptions } = opts;
  const optionsForFetch: RequestInit = fetchOptions as RequestInit;

  // Generate request ID at start (before fetch)
  const requestId = generateRequestId();
  const baseUrl = await getProxyBaseUrl();
  const url = baseUrl + endpoint;

  // Normalize endpoint for diagnostics (path-only, no query, no host)
  const endpointNormalized = diagnosticsStore.normalizeEndpoint(endpoint);
  const finalOpKey = opKey || deriveOpKey(endpointNormalized, optionsForFetch.method);

  // Record call start
  const callId = diagnosticsStore.recordCallStart(finalOpKey, endpointNormalized);
  const startTime = Date.now();

  const simpleGetSnapshot = isSimpleGetSnapshot(endpoint, optionsForFetch.method);
  let headers: HeadersInit;

  if (simpleGetSnapshot) {
    // No custom headers: avoids CORS preflight. Use only simple headers (Accept is safe).
    // Do NOT call getValidAccessToken for allowlisted endpoints.
    headers = { Accept: "application/json" };
  } else {
    const token = await getValidAccessToken();
    headers = {
      "Content-Type": "application/json",
      "x-request-id": requestId,
      ...(optionsForFetch.headers as Record<string, string>),
    };
    headers["x-optlab-client"] = "excel-addin";
    headers["x-optlab-tenant"] = "public";
    if (token) {
      headers["Authorization"] = "Bearer " + token;
    }
  }

  const requestOptions: RequestInit = {
    ...optionsForFetch,
    headers,
  };

  // Enforce timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000); // 30s timeout

  try {
    // Merge signals if both are provided (test mode may provide signal)
    if (requestOptions.signal) {
      // If both signals exist, create a combined signal that aborts if either aborts
      const combinedController = new AbortController();
      const abortIfNeeded = () => combinedController.abort();
      requestOptions.signal.addEventListener("abort", abortIfNeeded);
      controller.signal.addEventListener("abort", abortIfNeeded);
      requestOptions.signal = combinedController.signal;
    } else {
      requestOptions.signal = controller.signal;
    }
    const response = await fetch(url, requestOptions);
    clearTimeout(timeoutId);

    const ms = Date.now() - startTime;

    // Extract request ID from headers (try x-request-id, then x-correlation-id)
    // Track whether we got a header (vs using the generated requestId fallback)
    const headerRequestId =
      response.headers.get("x-request-id") ||
      response.headers.get("x-correlation-id");
    let responseRequestId = headerRequestId || requestId;

    // Capture cache headers for diagnostics
    const cacheResultRaw = response.headers.get("x-optlab-cache");
    const cacheBucket = (response.headers.get("x-optlab-cache-bucket") || "").trim();
    const cacheKeyRaw = response.headers.get("x-optlab-cache-key") || "";
    const cache = normalizeCacheResult(cacheResultRaw);
    const cacheKey = cacheKeyRaw.length > 60 ? cacheKeyRaw.substring(0, 60) : cacheKeyRaw;
    const cacheDiag = {
      cache,
      cacheBucket: cacheBucket || undefined,
      cacheKey: cacheKey || undefined,
      sessionCache: sessionCacheOutcome,
      sessionJoinCount: typeof sessionJoinCountGetter === "function" ? sessionJoinCountGetter() : 0,
    };

    // Increment cache metrics based on header
    if (cacheResultRaw) {
      const normalizedResult = cacheResultRaw.trim().toLowerCase();
      if (normalizedResult === "hit") {
        diagnosticsStore.incrementCacheMetric("backend_hit");
      } else if (normalizedResult === "miss") {
        diagnosticsStore.incrementCacheMetric("backend_miss");
      } else if (normalizedResult === "bypass") {
        diagnosticsStore.incrementCacheMetric("backend_bypass");
      } else if (normalizedResult === "stale") {
        diagnosticsStore.incrementCacheMetric("backend_stale");
      }
    }

    // Read response body as text first (can only read once)
    const responseText = await response.text();

    // If we're already parsing JSON (which we do below), check for request_id in JSON
    // This is safe because we're already consuming the response text
    let parsedData: any = null;
    let jsonRequestId: string | undefined = undefined;

    if (!response.ok) {
      let code: string;
      let message: string;
      let retryable: boolean;

      // Attempt to parse JSON as ErrorResponse
      try {
        parsedData = JSON.parse(responseText) as ErrorResponse;
        const errorData = parsedData;
        code = errorData.error?.code || "HTTP_ERROR";
        message = errorData.error?.message || "Request failed";
        const finalRequestId = errorData.error?.request_id || responseRequestId;
        responseRequestId = finalRequestId;

        // Check for request_id in parsed JSON (top-level or in error object)
        if (errorData.error?.request_id) {
          jsonRequestId = errorData.error.request_id;
        } else if ((parsedData as any).request_id && typeof (parsedData as any).request_id === "string") {
          jsonRequestId = (parsedData as any).request_id;
        } else if ((parsedData as any).requestId && typeof (parsedData as any).requestId === "string") {
          jsonRequestId = (parsedData as any).requestId;
        }

        // Use header request_id if available, otherwise use JSON, otherwise use generated requestId
        // (header takes precedence over JSON, JSON takes precedence over generated)
        const finalRequestIdForDiag = headerRequestId || jsonRequestId || requestId;

        // Determine retryable based on status + code (not message heuristics)
        // Do NOT retry local proxy rate limits (RATE_LIMIT_EXCEEDED) - retrying worsens bursts
        // Do NOT retry upstream 429 (UPSTREAM_RATE_LIMITED) for Excel bursts
        if (code === "RATE_LIMIT_EXCEEDED" || code === "UPSTREAM_RATE_LIMITED") {
          retryable = false;
        } else {
          retryable =
            response.status === 502 ||
            response.status === 503 ||
            response.status === 504 ||
            code === "UPSTREAM_TIMEOUT" ||
            code === "TIMEOUT" ||
            code === "BAD_PROXY_RESPONSE";
        }

        // Record diagnostics before throwing
        diagnosticsStore.recordCallEnd(callId, {
          ok: false,
          ms,
          request_id: finalRequestIdForDiag,
          ...cacheDiag,
        });
        diagnosticsStore.recordError({
          category: categorizeError(null, response.status),
          message: code + ": " + message, // Use actual error code and message, not just "HTTP 404"
          op_key: finalOpKey,
          endpoint: endpointNormalized,
          request_id: finalRequestIdForDiag,
        });

        logger.log(
          "ProxyClient",
          "op=" +
            finalOpKey +
            " endpoint=" +
            endpointNormalized +
            " ok=false ms=" +
            ms +
            " request_id=" +
            finalRequestIdForDiag +
            " cache=" +
            cache +
            " bucket=" +
            cacheBucket
        );

        const details = errorData.error && typeof errorData.error === "object"
          ? { ...(errorData.error as Record<string, unknown>), request_id: finalRequestId }
          : undefined;
        throw new ProxyClientError(code, message, response.status, finalRequestId, retryable, details);
      } catch (parseError) {
        // If parse fails, check if response body is HTML or non-JSON
        if (parseError instanceof ProxyClientError) {
          throw parseError;
        }

        const isHtml = responseText.trim().toLowerCase().startsWith("<!doctype") || responseText.trim().toLowerCase().startsWith("<html");
        if (isHtml || responseText.length === 0) {
          // Non-JSON response (e.g., Cloudflare error page)
          if (response.status === 502 || response.status === 503 || response.status === 504) {
            code = "BAD_PROXY_RESPONSE";
            retryable = true;
          } else {
            code = "HTTP_ERROR";
            retryable = false;
          }
        } else {
          code = "HTTP_ERROR";
          retryable = false;
        }

        message =
          "Server error: " + (responseText.substring(0, 100) || "Unknown error (status " + response.status + ")");

        // Record diagnostics before throwing
        diagnosticsStore.recordCallEnd(callId, {
          ok: false,
          ms,
          request_id: responseRequestId,
          ...cacheDiag,
        });
        diagnosticsStore.recordError({
          category: categorizeError(null, response.status),
          message: code + ": " + message, // Use actual error code and message
          op_key: finalOpKey,
          endpoint: endpointNormalized,
          request_id: responseRequestId,
        });

        logger.log(
          "ProxyClient",
          "op=" +
            finalOpKey +
            " endpoint=" +
            endpointNormalized +
            " ok=false ms=" +
            ms +
            " request_id=" +
            responseRequestId +
            " cache=" +
            cache +
            " bucket=" +
            cacheBucket
        );

        throw new ProxyClientError(code, message, response.status, responseRequestId, retryable);
      }
    }

    // Parse JSON for successful responses
    try {
      parsedData = JSON.parse(responseText) as T;

      // Check for request_id in parsed JSON (top-level only, shallow peek)
      if (!jsonRequestId) {
        if ((parsedData as any).request_id && typeof (parsedData as any).request_id === "string") {
          jsonRequestId = (parsedData as any).request_id;
        } else if ((parsedData as any).requestId && typeof (parsedData as any).requestId === "string") {
          jsonRequestId = (parsedData as any).requestId;
        }
      }

      // Use header request_id if available, otherwise use JSON, otherwise use generated requestId
      // (header takes precedence over JSON, JSON takes precedence over generated)
      const finalRequestIdForDiag = headerRequestId || jsonRequestId || requestId;

      // Record successful call
      diagnosticsStore.recordCallEnd(callId, {
        ok: true,
        ms,
        request_id: finalRequestIdForDiag,
        ...cacheDiag,
      });
      diagnosticsStore.setLastRequest({
        request_id: finalRequestIdForDiag,
        op_key: finalOpKey,
        endpoint: endpointNormalized,
        ms,
        ok: true,
      });

      logger.log(
        "ProxyClient",
        "op=" +
          finalOpKey +
          " endpoint=" +
          endpointNormalized +
          " ok=true ms=" +
          ms +
          " request_id=" +
          finalRequestIdForDiag +
          " cache=" +
          cache +
          " bucket=" +
          cacheBucket
      );

      return parsedData;
    } catch (parseError) {
      logger.error("ProxyClient", "failed to parse response as JSON", parseError);

      // Record error
      diagnosticsStore.recordCallEnd(callId, {
        ok: false,
        ms,
        request_id: responseRequestId,
        ...cacheDiag,
      });
      diagnosticsStore.recordError({
        category: "backend",
        message: "Invalid JSON response",
        op_key: finalOpKey,
        endpoint: endpointNormalized,
        request_id: responseRequestId,
      });

      throw new ProxyClientError("BAD_JSON", "Invalid JSON response from server", 502, responseRequestId, false);
    }
  } catch (error) {
    clearTimeout(timeoutId);

    const ms = Date.now() - startTime;

    // Handle network/DNS failures and timeouts
    if (error instanceof ProxyClientError) {
      // Already recorded in the error path above
      throw error;
    }

    const category = categorizeError(error);
    const message = safeErrorMessage(error);

    // Record error (no response: no cache headers)
    diagnosticsStore.recordCallEnd(callId, {
      ok: false,
      ms,
      sessionCache: sessionCacheOutcome,
      sessionJoinCount: 0,
    });
    diagnosticsStore.recordError({
      category,
      message,
      op_key: finalOpKey,
      endpoint: endpointNormalized,
    });

    logger.log(
      "ProxyClient",
      "op=" +
        finalOpKey +
        " endpoint=" +
        endpointNormalized +
        " ok=false ms=" +
        ms +
        " error=" +
        message
    );

    if (error instanceof Error && error.name === "AbortError") {
      throw new ProxyClientError("TIMEOUT", "Request timeout", 0, requestId, true, { cause: error });
    }

    // Network/DNS failure
    // Clear timeout if fetch threw (timeout may still be pending)
    clearTimeout(timeoutId);
    
    logger.error("ProxyClient", "request failed", error);
    throw new ProxyClientError("NETWORK_ERROR", "Network request failed", 0, requestId, true, { cause: error });
  }
}

// Retry wrapper for idempotent GET/HEAD requests
export async function requestWithRetry<T extends ProxyResponse>(
  endpoint: string,
  options: RequestInit | ProxyRequestOptions = {},
  opKey?: string
): Promise<T> {
  const method = (options as RequestInit).method || "GET";
  const isIdempotent = method === "GET" || method === "HEAD" || method === undefined;

  if (!isIdempotent) {
    // Non-idempotent requests: no retry
    return request<T>(endpoint, options, opKey);
  }

  // Test mode: disable retries for faster tests
  // @ts-ignore - process.env may be injected by bundler
  const isTestMode = typeof process !== "undefined" && process.env && process.env.NODE_ENV === "test";
  const maxAttempts = isTestMode ? 1 : 3;
  let lastError: ProxyClientError | Error | null = null;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await request<T>(endpoint, options, opKey);
    } catch (error) {
      lastError = error instanceof ProxyClientError ? error : new ProxyClientError(
        "UNKNOWN_ERROR",
        error instanceof Error ? error.message : "Unknown error",
        0,
        generateRequestId(),
        false,
        { cause: error }
      );

      // Only retry if error is ProxyClientError and retryable
      if (!(error instanceof ProxyClientError) || !error.retryable) {
        throw error;
      }

      // Don't retry on last attempt
      if (attempt >= maxAttempts) {
        throw error;
      }

      // Test mode: skip backoff delays
      if (isTestMode) {
        continue; // No delay in test mode
      }

      // Calculate backoff: 250ms, 750ms (+ jitter up to 200ms)
      const baseDelay = attempt === 1 ? 250 : 750;
      const jitter = Math.floor(Math.random() * 200);
      const delay = baseDelay + jitter;

      // Wait before retry
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  // Should never reach here, but TypeScript needs it
  throw lastError || new Error("Request failed after retries");
}

/**
 * Ping the proxy
 */
export async function ping(): Promise<PingResponse> {
  return requestWithRetry<PingResponse>("/v1/test/ping", { method: "GET" });
}

/**
 * Test authentication
 */
export async function testAuth(): Promise<AuthTestResponse> {
  return requestWithRetry<AuthTestResponse>("/v1/test/auth", { method: "GET" });
}

/**
 * Search Massive.com API
 */
export async function searchMassive(q: string): Promise<MassiveSearchResponse> {
  const encodedQ = encodeURIComponent(q);
  return requestWithRetry<MassiveSearchResponse>("/v1/massive/search?q=" + encodedQ, { method: "GET" });
}

/**
 * Get Massive.com item by ID
 */
export async function getMassiveItem(id: string): Promise<MassiveItemResponse> {
  const encodedId = encodeURIComponent(id);
  return requestWithRetry<MassiveItemResponse>("/v1/massive/item/" + encodedId, { method: "GET" });
}

/**
 * Get Massive.com market holidays (upcoming)
 */
export async function getMarketHolidaysUpcoming(): Promise<MassiveMarketHolidaysResponse> {
  return requestWithRetry<MassiveMarketHolidaysResponse>(
    "/v1/massive/market-holidays/upcoming",
    { method: "GET" },
    "TOOLS.HOLIDAYS"
  );
}

// Market status "now": short TTL (5–15s) + single-flight to avoid recalc storms
const MARKET_STATUS_NOW_TTL_MS = 10 * 1000; // 10 seconds
const marketStatusNowCache = new Map<string, { value: MassiveMarketStatusNowResponse; expiresAt: number }>();
const marketStatusNowInflight = new Map<string, Promise<MassiveMarketStatusNowResponse>>();
const MARKET_STATUS_NOW_KEY = "marketstatus:now";

/**
 * Get Massive.com market status (now): is market open, optional next_open/next_close.
 * Single-flight + short TTL cache so many cells recalc in the same tick share one request.
 */
export async function getMarketStatusNow(): Promise<MassiveMarketStatusNowResponse> {
  await diagnosticsStore.init();
  const endpoint = "/v1/massive/marketstatus/now";
  const endpointNormalized = diagnosticsStore.normalizeEndpoint(endpoint);

  const now = Date.now();
  const cached = marketStatusNowCache.get(MARKET_STATUS_NOW_KEY);
  if (cached && cached.expiresAt > now) {
    const callId = diagnosticsStore.recordCallStart("MARKET.STATUS_NOW", endpointNormalized);
    diagnosticsStore.recordCallEnd(callId, { ok: cached.value.ok, ms: 0, request_id: (cached.value as any).request_id ?? "cache-hit" });
    return cached.value;
  }
  if (cached) {
    marketStatusNowCache.delete(MARKET_STATUS_NOW_KEY);
  }

  const inflight = marketStatusNowInflight.get(MARKET_STATUS_NOW_KEY);
  if (inflight) {
    return inflight;
  }

  const p = enqueue(async () => {
    try {
      const res = await requestWithRetry<MassiveMarketStatusNowResponse>(
        endpoint,
        { method: "GET" },
        "MARKET.STATUS_NOW"
      );
      marketStatusNowCache.set(MARKET_STATUS_NOW_KEY, { value: res, expiresAt: now + MARKET_STATUS_NOW_TTL_MS });
      return res;
    } finally {
      marketStatusNowInflight.delete(MARKET_STATUS_NOW_KEY);
    }
  });
  marketStatusNowInflight.set(MARKET_STATUS_NOW_KEY, p);
  return p;
}

/**
 * Get the current proxy base URL in use
 * Note: This is async now, but kept for backward compatibility
 * Consider using getPublicConfig() directly in new code
 */
export async function getProxyBaseUrlInUse(): Promise<string> {
  return await getProxyBaseUrl();
}

/**
 * GET /v1/entitlements/me - current user's entitlement (always returns a row).
 */
export async function getEntitlementsMe(): Promise<EntitlementsMeResponse> {
  return requestWithRetry<EntitlementsMeResponse>("/v1/entitlements/me", { method: "GET" });
}

/**
 * POST /v1/stripe/checkout-session - returns checkout URL or billing portal URL when already subscribed.
 * @param interval - "monthly" or "annual". Default "monthly".
 */
export async function createCheckoutSession(
  interval: "monthly" | "annual" = "monthly"
): Promise<CheckoutSessionResponse> {
  return requestWithRetry<CheckoutSessionResponse>("/v1/stripe/checkout-session", {
    method: "POST",
    body: JSON.stringify({ interval }),
  });
}

/**
 * POST /v1/stripe/billing-portal - returns billing portal URL for managing/canceling subscription.
 */
export async function createBillingPortalSession(): Promise<BillingPortalResponse> {
  return requestWithRetry<BillingPortalResponse>("/v1/stripe/billing-portal", {
    method: "POST",
    body: JSON.stringify({}),
  });
}

/**
 * Get v3 ticker reference data
 */
export async function getV3Ticker(ticker: string): Promise<ProxyResponse> {
  const encodedTicker = encodeURIComponent(ticker);
  return requestWithRetry<ProxyResponse>("/v1/massive/v3/reference/tickers/" + encodedTicker, { method: "GET" });
}

/**
 * Get v3 options contracts
 */
export async function getV3OptionsContracts(params: {
  underlying_ticker: string;
  limit?: number;
  order?: string;
  sort?: string;
}): Promise<ProxyResponse> {
  const searchParams = new URLSearchParams();
  searchParams.set("underlying_ticker", params.underlying_ticker);
  if (params.limit !== undefined) {
    searchParams.set("limit", params.limit.toString());
  }
  if (params.order) {
    searchParams.set("order", params.order);
  }
  if (params.sort) {
    searchParams.set("sort", params.sort);
  }
  const queryString = searchParams.toString();
  return requestWithRetry<ProxyResponse>("/v1/massive/v3/reference/options/contracts?" + queryString, {
    method: "GET",
  });
}

/**
 * Get open/close (single-day OHLC) for a ticker on a date.
 * @deprecated Use getOpenCloseByDateCached for better performance in Excel burst scenarios
 */
export async function getOpenCloseByDate(ticker: string, date: string): Promise<ProxyResponse> {
  const t = encodeURIComponent(ticker.trim().toUpperCase());
  const d = encodeURIComponent(date.trim());
  return requestWithRetry<ProxyResponse>("/v1/massive/v1/open-close/" + t + "/" + d, { method: "GET" });
}

/**
 * Get open/close (single-day OHLC) for a ticker on a date with caching, de-duplication, and concurrency limiting.
 * This version is optimized for Excel burst scenarios where many cells call the same (ticker, date) combination.
 *
 * Features:
 * - In-memory cache (10 minute TTL) to avoid duplicate network calls
 * - In-flight de-duplication to prevent concurrent requests for the same key
 * - Concurrency limiting (max 5 concurrent requests) to prevent overwhelming proxy/upstream
 *
 * @param ticker Ticker symbol (e.g., "AAPL")
 * @param date Date in YYYY-MM-DD format
 * @returns The data payload (res.data) from the upstream response
 */
export async function getOpenCloseByDateCached(ticker: string, date: string): Promise<any> {
  const t = String(ticker || "").trim().toUpperCase();
  const d = String(date || "").trim();
  const key = "openclose:" + t + ":" + d;

  // Ensure diagnostics store is initialized (idempotent)
  await diagnosticsStore.init();

  // Normalize endpoint for diagnostics
  const endpointNormalized = diagnosticsStore.normalizeEndpoint(
    "/v1/massive/v1/open-close/" + encodeURIComponent(t) + "/" + encodeURIComponent(d)
  );

  // Debug logging to verify function is being called
  // Debug logging (silenced in test mode)
  logDebug("[getOpenCloseByDateCached] called ticker=" + t + " date=" + d + " opKey=PRICES.HISTORY");

  // 1) Check cache (with expiration)
  const cached = openCloseCache.get(key);
  const now = Date.now();
  if (cached && cached.expiresAt > now) {
    // Cache hit - record in diagnostics (no HTTP request, but function was called)
    const cacheCallId = diagnosticsStore.recordCallStart("PRICES.HISTORY", endpointNormalized);
    const cacheStartTime = Date.now();
    const ms = Date.now() - cacheStartTime;
    diagnosticsStore.recordCallEnd(cacheCallId, {
      ok: true,
      ms: ms,
      request_id: "cache-hit",
    });
    diagnosticsStore.setLastRequest({
      request_id: "cache-hit",
      op_key: "PRICES.HISTORY",
      endpoint: endpointNormalized,
      ms: ms,
      ok: true,
    });
    return cached.value;
  } else if (cached) {
    // Expired entry - remove it
    openCloseCache.delete(key);
  }

  // 2) Check in-flight de-duplication
  const inflight = openCloseInflight.get(key);
  if (inflight) {
    // Deduplicated - waiting on existing request
    // The original request will record diagnostics, no need to record again here
    return inflight;
  }

  // 3) Schedule with concurrency limiter
  const p = enqueue(async () => {
    try {
      const res: any = await requestWithRetry<any>(
        "/v1/massive/v1/open-close/" + encodeURIComponent(t) + "/" + encodeURIComponent(d),
        { method: "GET" },
        "PRICES.HISTORY"
      );

      // Cache successful payload (res.data is the upstream JSON)
      // Handle cases where data might be null or undefined (return empty object as fallback)
      const value = res && res.data !== undefined && res.data !== null ? res.data : {};
      // Debug logging
      // Debug logging (silenced in test mode)
      logDebug("[getOpenCloseByDateCached] res.ok=" + (res ? res.ok : "N/A") + " hasData=" + (res && res.data ? "yes" : "no") + " dataType=" + typeof value);
      openCloseCache.set(key, { value, expiresAt: now + CACHE_TTL_MS });
      
      // Note: The HTTP request already recorded its own diagnostics with request_id in request()
      // No need to record again here to avoid duplicates
      
      return value;
    } catch (error) {
      // Note: The HTTP request already recorded error diagnostics in request()
      // Don't record again here to avoid duplicates - the error is already captured
      // Re-throw error so it can be caught by the Excel function's error handler
      throw error;
    } finally {
      // Always remove from in-flight map, even on error
      openCloseInflight.delete(key);
    }
  });

  // Store in-flight promise for de-duplication
  openCloseInflight.set(key, p);
  return p;
}
